import { motion } from "framer-motion";
import { MessageCircle, MapPin, ChevronDown, ShieldCheck, Star } from "lucide-react";
import { SITE, WHATSAPP_URL, MAPS_URL } from "@/constants/site";

export default function Hero() {
  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex items-center justify-center"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1761229170508-f4791c297af8?w=1600&q=70&auto=format&fit=crop"
          alt=""
          width="1600"
          height="1075"
          loading="eager"
          decoding="async"
          fetchPriority="high"
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />
        <div className="absolute inset-0 diagonal-stripes" />
        {/* Red glow */}
        <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-red-600/20 blur-[140px] animate-red-pulse" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-red-700/10 blur-[120px]" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left: Text */}
          <div className="lg:col-span-7 text-left">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="inline-flex items-center gap-2 mb-6 border border-red-500/30 bg-red-600/10 px-4 py-2 backdrop-blur-sm"
            >
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              <span className="text-xs uppercase tracking-[0.25em] text-red-200 font-semibold">
                Oficina Premium · Jundiaí/SP
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black uppercase leading-[0.95] tracking-tighter text-white"
            >
              Força total
              <br />
              em cada{" "}
              <span className="relative inline-block">
                <span className="text-red-600">câmbio.</span>
                <span className="absolute -bottom-2 left-0 w-full h-1 bg-red-600 red-glow" />
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.15 }}
              className="mt-8 text-lg md:text-xl text-zinc-400 max-w-2xl leading-relaxed font-body"
            >
              Especialistas em <span className="text-white font-semibold">câmbio automático</span> e mecânica em geral. Diagnóstico preciso, atendimento transparente e manutenção automotiva com padrão premium para quem busca confiança e qualidade.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.25 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                className="group inline-flex items-center justify-center gap-3 bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-7 text-sm uppercase tracking-[0.2em] transition-all duration-300 border border-red-500/60 shadow-[0_0_20px_rgba(229,57,53,0.35)] hover:shadow-[0_0_40px_rgba(229,57,53,0.6)]"
              >
                <MessageCircle size={18} strokeWidth={2} />
                Agendar pelo WhatsApp
                <span className="w-1.5 h-1.5 bg-white rounded-full group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-location-btn"
                className="inline-flex items-center justify-center gap-3 bg-transparent hover:bg-white/5 text-white font-bold py-4 px-7 text-sm uppercase tracking-[0.2em] transition-all duration-300 border border-zinc-700 hover:border-zinc-400"
              >
                <MapPin size={18} strokeWidth={2} /> Ver Localização
              </a>
            </motion.div>

            {/* Trust badges */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.35 }}
              className="mt-12 grid grid-cols-2 sm:grid-cols-3 gap-6 max-w-xl"
            >
              <div className="flex items-center gap-3">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={14} className="fill-red-500 text-red-500" />
                  ))}
                </div>
                <div>
                  <p className="text-white font-bold text-sm">5.0</p>
                  <p className="text-xs text-zinc-500">Google</p>
                </div>
              </div>
              <div className="flex items-center gap-3 border-l border-white/10 pl-6">
                <ShieldCheck className="text-red-500" size={22} strokeWidth={1.5} />
                <div>
                  <p className="text-white font-bold text-sm">Garantia</p>
                  <p className="text-xs text-zinc-500">de qualidade</p>
                </div>
              </div>
              <div className="flex items-center gap-3 border-l border-white/10 pl-6 col-span-2 sm:col-span-1">
                <div className="font-display text-2xl text-red-500 font-black">+10</div>
                <div>
                  <p className="text-white font-bold text-sm">Anos</p>
                  <p className="text-xs text-zinc-500">de experiência</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right: Logo Showcase */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-5 relative flex items-center justify-center"
          >
            <div className="relative w-full max-w-md aspect-square">
              {/* Spinning glow */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-red-600/30 via-transparent to-red-600/10 blur-3xl animate-red-pulse" />
              {/* Metallic frame */}
              <div className="absolute inset-6 rounded-full border border-white/5 backdrop-blur-md bg-gradient-to-br from-zinc-900/60 to-black/60" />
              <div className="absolute inset-12 rounded-full border border-red-600/20" />
              {/* Logo */}
              <div className="absolute inset-0 flex items-center justify-center p-10">
                <img
                  src={SITE.logo}
                  alt="Full Force Car Premium"
                  width="500"
                  height="500"
                  fetchPriority="high"
                  decoding="async"
                  className="w-full h-auto object-contain"
                  style={{
                    filter:
                      "invert(1) hue-rotate(180deg) drop-shadow(0 0 30px rgba(229,57,53,0.45))",
                  }}
                />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.a
          href="#sobre"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-zinc-500 hover:text-white transition-colors group"
          data-testid="hero-scroll-indicator"
        >
          <span className="text-[10px] uppercase tracking-[0.3em] font-semibold">Role</span>
          <ChevronDown size={20} className="animate-bounce" />
        </motion.a>
      </div>
    </section>
  );
}
